/**
 * The management surface's vocabulary — portal, cancel, change-plan.
 *
 * Three endpoints, one file, because they are one decision: **what a subscriber
 * may do to a subscription that already exists.** The split between them is not
 * arbitrary either — money mechanics (cards, invoices) go to Stripe's hosted
 * portal, and the two actions with product consequences stay in our UI, where
 * the exit survey and the save offer live (`stripe-hosted-vs-custom.md`).
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md — `POST /billing/portal-session`, `POST /billing/cancel`, `POST /billing/change-plan`
 * @see docs/projects/active/refactor-payments/milestones.md
 */
import { Schema } from 'effect';
import {
  CentAmount,
  IsoInstant,
  PlanKey,
} from '@listening/api-contract/schemas/billing-status';

/**
 * The two portal destinations, and the whole of the portal we use.
 *
 * `subscription_cancel` and `subscription_update` are deliberately absent — they
 * are disabled on the configuration itself (see the portal declaration in
 * `@listening/backend/stripe/portal-config-declaration`), so naming them here
 * would be naming something the portal will refuse. A flow the client cannot ask
 * for is one fewer way for cancellation to escape the exit survey.
 *
 * `invoice_history` has no Stripe *flow*: it is a plain session against the
 * least-privilege configuration, whose home page contains invoices, card and
 * billing details and nothing else.
 */
export const PortalFlow = Schema.Literals([
  'payment_method_update',
  'invoice_history',
]);
export type PortalFlow = typeof PortalFlow.Type;

export const PortalSessionRequest = Schema.Struct({ flow: PortalFlow });
export type PortalSessionRequest = typeof PortalSessionRequest.Type;

export const PortalSessionResponse = Schema.Struct({
  /**
   * A full-redirect target, never an iframe src, and never cached: the session
   * expires five minutes after minting.
   */
  url: Schema.String,
});
export type PortalSessionResponse = typeof PortalSessionResponse.Type;

export const CancelRequest = Schema.Struct({
  /** Free text from the exit survey, carried to Stripe as the cancellation comment. */
  reason: Schema.optional(Schema.NullOr(Schema.String)),
});
export type CancelRequest = typeof CancelRequest.Type;

export const CancelResponse = Schema.Struct({
  status: Schema.Literals(['canceled']),
  /**
   * When access actually ends — a UTC instant, always present.
   *
   * It is not always the subscription's period end: a prepaid subscriber's
   * access runs to their recorded `paid_through_at`, years past the next
   * boundary. Any branch that cannot name this date fails with an error rather
   * than guessing, because the guess is what tells someone with 18 months left
   * that they have a week.
   */
  access_until: IsoInstant,
});
export type CancelResponse = typeof CancelResponse.Type;

/**
 * The three ways a plan change bills, decided server-side from the live
 * subscription and the two prices.
 *
 * - `immediate_invoice`: the interval changes on a paying subscription. The
 *   anchor resets to now and an invoice is created on confirm.
 * - `next_invoice`: same interval. Prorations ride the next renewal invoice.
 * - `trial`: the subscription is trialing. Nothing is prorated; the trial keeps
 *   its clock and the first invoice at trial end is for the new plan.
 */
export const PlanChangeTerms = Schema.Literals([
  'immediate_invoice',
  'next_invoice',
  'trial',
]);
export type PlanChangeTerms = typeof PlanChangeTerms.Type;

/** One line of the previewed invoice. Signed: proration credits are negative. */
export const PlanChangeLine = Schema.Struct({
  description: Schema.String,
  amount_cents: Schema.Int,
  period: Schema.Struct({ start: IsoInstant, end: IsoInstant }),
});
export type PlanChangeLine = typeof PlanChangeLine.Type;

export const NextCharge = Schema.Struct({
  amount_cents: CentAmount,
  at: IsoInstant,
});
export type NextCharge = typeof NextCharge.Type;

/**
 * What confirming a plan change will do, computed by Stripe's invoice preview
 * under exactly the terms the change itself will state.
 *
 * `proration_date` is the instant the prorations were computed at. The client
 * sends it back with `expected_due_today_cents` on confirm, which is what lets
 * the server refuse a change whose cost has moved since it was shown.
 */
export const PlanChangePreview = Schema.Struct({
  plan_id: PlanKey,
  currency: Schema.String,
  terms: PlanChangeTerms,
  /** Charged the moment the change is confirmed. 0 under `next_invoice` and `trial`. */
  due_today_cents: CentAmount,
  /**
   * Booked to the customer's credit balance when prorations exceed today's
   * charge — a downgrade — after any unpaid earlier balance is settled from it.
   * Consumed by later invoices, never refunded to a card.
   */
  credit_cents: CentAmount,
  /**
   * The customer's existing balance as today's invoice consumes it, signed so
   * that the line items plus this plus `credit_cents` equal `due_today_cents`:
   * negative when earlier credit lowers the charge, positive when an unpaid
   * earlier balance is added to it. 0 under `next_invoice` and `trial`.
   */
  balance_applied_cents: Schema.Int,
  lines: Schema.Array(PlanChangeLine),
  /**
   * The first charge after today: the new plan's price at the new anchor, the
   * unchanged trial end, or the next renewal. Null when nothing follows.
   */
  next_charge: Schema.NullOr(NextCharge),
  /**
   * Pending invoice items already on the customer from an earlier change.
   * Flexible billing mode folds them into the next invoice the subscription
   * generates; classic mode leaves them for the next renewal.
   */
  pending_items_cents: Schema.Int,
  proration_date: IsoInstant,
});
export type PlanChangePreview = typeof PlanChangePreview.Type;

export const PreviewPlanChangeRequest = Schema.Struct({ plan_id: PlanKey });
export type PreviewPlanChangeRequest = typeof PreviewPlanChangeRequest.Type;

export const ChangePlanRequest = Schema.Struct({
  /**
   * A plan, never a price id: the closed write vocabulary that replaced
   * `modify-subscription`'s `stripe_price_id` — a free string the client chose
   * and the server charged whatever it named.
   */
  plan_id: PlanKey,
  /**
   * From the preview the person confirmed. Required, not optional: a request
   * shape that changes plan without a preview is one that moves money
   * unannounced. Stripe reproduces the previewed prorations from this instant,
   * and the server refuses when the recomputed amount due differs.
   */
  proration_date: IsoInstant,
  expected_due_today_cents: CentAmount,
});
export type ChangePlanRequest = typeof ChangePlanRequest.Type;

export const ChangePlanResponse = Schema.Struct({
  status: Schema.Literals(['changed']),
  /** When the new plan is in force — immediate, so a UTC instant of now. */
  effective_at: IsoInstant,
  /** What was charged, read off the invoice the change created. 0 when none was. */
  charged_cents: CentAmount,
  next_charge: Schema.NullOr(NextCharge),
});
export type ChangePlanResponse = typeof ChangePlanResponse.Type;
